import type { FishingSession } from '@/world/fishing';

/**
 * Fishing prompts and the strike bar.
 *
 * DOM rather than canvas so the bar is crisp at any scale and can be sized in
 * real pixels — a timing target rendered at the 384px virtual width would be
 * about twelve pixels wide on a phone.
 */
export class FishingUi {
  private readonly root: HTMLDivElement;
  private readonly prompt: HTMLDivElement;
  private readonly bar: HTMLDivElement;
  private readonly zone: HTMLDivElement;
  private readonly marker: HTMLDivElement;
  private lastPhase = '';

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'fishing';
    this.root.hidden = true;

    this.prompt = document.createElement('div');
    this.prompt.className = 'fishing-prompt';

    this.bar = document.createElement('div');
    this.bar.className = 'fishing-bar';
    this.zone = document.createElement('div');
    this.zone.className = 'fishing-zone';
    this.marker = document.createElement('div');
    this.marker.className = 'fishing-marker';
    this.bar.append(this.zone, this.marker);

    this.root.append(this.prompt, this.bar);
    container.appendChild(this.root);
  }

  update(session: FishingSession | null): void {
    if (!session || session.isOver) {
      this.root.hidden = true;
      this.lastPhase = '';
      // Clear rather than just hide: stale text flashes for a frame when the
      // next cast opens, and it makes the element lie to anything reading it.
      this.prompt.textContent = '';
      this.prompt.classList.remove('is-bite');
      this.bar.hidden = true;
      return;
    }

    this.root.hidden = false;

    if (session.phase !== this.lastPhase) {
      this.lastPhase = session.phase;
      this.prompt.textContent =
        session.phase === 'casting'
          ? 'Waiting…'
          : session.phase === 'bite'
            ? '!'
            : 'Now!';
      this.prompt.classList.toggle('is-bite', session.phase === 'bite');
      this.bar.hidden = session.phase !== 'landing';

      if (session.phase === 'landing') {
        const width = session.targetHalfWidth * 2 * 100;
        this.zone.style.width = `${width}%`;
        this.zone.style.left = `${session.targetCentre * 100 - width / 2}%`;
      }
    }

    if (session.phase === 'landing') {
      this.marker.style.left = `${session.marker * 100}%`;
    }
  }

  destroy(): void {
    this.root.remove();
  }
}
